import React, { useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import { formatPhone } from '../../utils/phone';
import CustomerSuggestions, { suggestionCount } from './CustomerSuggestions';
import CustomerMerge, { findDuplicates } from './CustomerMerge';   // merge duplicate customers (Oct 6 2026)
const CAN_MERGE = (() => { try { return ['admin', 'Admin', 'Executive', 'Accounting'].includes(JSON.parse(localStorage.getItem('titan_session') || 'null')?.role); } catch { return false; } })();
const NOT_DUPES_KEY = 'titan_not_duplicate_customers';
const readNotDupes = () => { try { return JSON.parse(localStorage.getItem(NOT_DUPES_KEY) || '[]') || []; } catch { return []; } };   // web-found addresses/phones to confirm (Oct 6 2026)

const API_BASE = process.env.REACT_APP_API_BASE || 'https://app.titanelectricreno.com:8080/api';

function getToken() {
  try { return JSON.parse(localStorage.getItem('titan_session') || 'null')?.token; } catch { return null; }
}

async function apiFetch(path, opts = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: { 'x-worker-token': getToken(), 'Content-Type': 'application/json', ...(opts.headers || {}) },
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

const EMPTY_CLIENT = { name: '', contact: '', address1: '', address2: '', phone: '', fax: '', notes: '' };
const EMPTY_CONTACT = { name: '', title: '', phone: '', email: '' };

// ── Client add/edit modal ─────────────────────────────────────────────────────
function ClientModal({ client, onClose, onSave }) {
  const [form, setForm] = useState(client ? { ...client } : { ...EMPTY_CLIENT });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim()) return setErr('Company name is required');
    setSaving(true); setErr('');
    try {
      const result = client
        ? await apiFetch(`/clients/${client.id}`, { method: 'PATCH', body: JSON.stringify(form) })
        : await apiFetch('/clients', { method: 'POST', body: JSON.stringify(form) });
      onSave(result);
    } catch (ex) { setErr(ex.message); } finally { setSaving(false); }
  }

  const field = (label, key, opts = {}) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</label>
      {opts.type === 'textarea' ? (
        <textarea value={form[key] || ''} onChange={e => set(key, e.target.value)} rows={3}
          style={{ border: '1px solid #e5e7eb', borderRadius: 7, padding: '8px 10px', fontSize: 13, resize: 'vertical', fontFamily: 'inherit' }} />
      ) : (
        <input type="text" value={form[key] || ''} onChange={e => set(key, e.target.value)}
          style={{ border: '1px solid #e5e7eb', borderRadius: 7, padding: '8px 10px', fontSize: 13 }} />
      )}
    </div>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 14, width: 560, maxWidth: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>{client ? 'Edit Client' : 'New Client'}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#9ca3af' }}>×</button>
        </div>
        <form onSubmit={handleSubmit} style={{ overflowY: 'auto', flex: 1, padding: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {field('Company Name *', 'name')}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {field('Phone', 'phone')}
            {field('Fax', 'fax')}
          </div>
          {field('Address Line 1', 'address1')}
          {field('Address Line 2 (City, State ZIP)', 'address2')}
          {field('Notes', 'notes', { type: 'textarea' })}
          {err && <div style={{ color: '#dc2626', fontSize: 13 }}>{err}</div>}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', paddingTop: 4 }}>
            <button type="button" onClick={onClose} style={{ padding: '9px 18px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
            <button type="submit" disabled={saving} style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: 'var(--orange)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              {saving ? 'Saving…' : 'Save Client'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Contacts management modal (per client) ────────────────────────────────────
function ContactsModal({ client, onClose }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingContact, setEditingContact] = useState(null); // null | 'new' | contact obj
  const [form, setForm] = useState({ ...EMPTY_CONTACT });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    apiFetch(`/clients/${client.id}/contacts`)
      .then(setContacts)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [client.id]);

  function openNew() { setForm({ ...EMPTY_CONTACT }); setEditingContact('new'); setErr(''); }
  function openEdit(c) { setForm({ name: c.name, title: c.title || '', phone: c.phone || '', email: c.email || '' }); setEditingContact(c); setErr(''); }
  function cancelEdit() { setEditingContact(null); setErr(''); }

  async function saveContact(e) {
    e.preventDefault();
    if (!form.name.trim()) return setErr('Name is required');
    setSaving(true); setErr('');
    try {
      if (editingContact === 'new') {
        const c = await apiFetch(`/clients/${client.id}/contacts`, { method: 'POST', body: JSON.stringify(form) });
        setContacts(cs => [...cs, c]);
      } else {
        const c = await apiFetch(`/clients/${client.id}/contacts/${editingContact.id}`, { method: 'PATCH', body: JSON.stringify(form) });
        setContacts(cs => cs.map(x => x.id === c.id ? c : x));
      }
      setEditingContact(null);
    } catch (ex) { setErr(ex.message); } finally { setSaving(false); }
  }

  async function deleteContact(c) {
    setDeleting(c.id);
    try {
      await apiFetch(`/clients/${client.id}/contacts/${c.id}`, { method: 'DELETE' });
      setContacts(cs => cs.filter(x => x.id !== c.id));
    } catch (ex) { alert(ex.message); } finally { setDeleting(null); }
  }

  const inputStyle = { border: '1px solid #e5e7eb', borderRadius: 7, padding: '7px 10px', fontSize: 13, width: '100%', boxSizing: 'border-box' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 14, width: 620, maxWidth: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>Contacts — {client.name}</h3>
            <p style={{ margin: '3px 0 0', fontSize: 12, color: '#9ca3af' }}>These appear as a dropdown when this client is selected on a bid.</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#9ca3af' }}>×</button>
        </div>

        <div style={{ overflowY: 'auto', flex: 1, padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Inline add/edit form */}
          {editingContact && (
            <form onSubmit={saveContact} style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                {editingContact === 'new' ? 'New Contact' : `Editing: ${editingContact.name}`}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', display: 'block', marginBottom: 3 }}>Name *</label>
                  <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={inputStyle} placeholder="Full name" />
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', display: 'block', marginBottom: 3 }}>Title</label>
                  <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} style={inputStyle} placeholder="e.g. Project Manager" />
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', display: 'block', marginBottom: 3 }}>Phone</label>
                  <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} onBlur={e => setForm(f => ({ ...f, phone: formatPhone(e.target.value) }))} style={inputStyle} placeholder="(775) 555-1234" />
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', display: 'block', marginBottom: 3 }}>Email</label>
                  <input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} style={inputStyle} placeholder="name@company.com" type="email" />
                </div>
              </div>
              {err && <div style={{ color: '#dc2626', fontSize: 12 }}>{err}</div>}
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="submit" disabled={saving}
                  style={{ padding: '7px 16px', borderRadius: 7, border: 'none', background: 'var(--orange)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                  {saving ? 'Saving…' : 'Save Contact'}
                </button>
                <button type="button" onClick={cancelEdit}
                  style={{ padding: '7px 16px', borderRadius: 7, border: '1px solid #e5e7eb', background: '#fff', fontSize: 13, cursor: 'pointer' }}>
                  Cancel
                </button>
              </div>
            </form>
          )}

          {/* Contact list */}
          {loading ? (
            <div style={{ color: '#9ca3af', textAlign: 'center', padding: 24 }}>Loading…</div>
          ) : contacts.length === 0 ? (
            <div style={{ color: '#9ca3af', textAlign: 'center', padding: 24, fontSize: 13 }}>
              No contacts yet. Add one above.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {contacts.map(c => (
                <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', border: '1px solid #e5e7eb', borderRadius: 9, background: '#fff' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, color: '#111' }}>{c.name}</div>
                    <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                      {c.title && <span>{c.title}</span>}
                      {c.phone && <span>{formatPhone(c.phone)}</span>}
                      {c.email && <span>{c.email}</span>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button onClick={() => openEdit(c)}
                      style={{ padding: '4px 10px', fontSize: 12, borderRadius: 6, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer' }}>
                      Edit
                    </button>
                    <button onClick={() => deleteContact(c)} disabled={deleting === c.id}
                      style={{ padding: '4px 10px', fontSize: 12, borderRadius: 6, border: '1px solid #fca5a5', background: '#fff', color: '#dc2626', cursor: 'pointer' }}>
                      {deleting === c.id ? '…' : 'Delete'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 24px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between' }}>
          <button onClick={openNew}
            style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: 'var(--orange)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            + Add Contact
          </button>
          <button onClick={onClose}
            style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', fontSize: 13, cursor: 'pointer' }}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function AdminClients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null);       // null | 'new' | client obj (edit)
  const [contactsFor, setContactsFor] = useState(null); // client obj | null
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [merging, setMerging] = useState(null);     // null | { start, other }
  const [notDupes, setNotDupes] = useState(readNotDupes);
  const [showDupes, setShowDupes] = useState(false);
  const dupes = CAN_MERGE ? findDuplicates(clients).filter(d => !notDupes.includes([d.a.id, d.b.id].sort().join('|'))) : [];
  function notSame(d) { const k = [d.a.id, d.b.id].sort().join('|'); const n = [...notDupes, k]; setNotDupes(n); try { localStorage.setItem(NOT_DUPES_KEY, JSON.stringify(n)); } catch { /* fine */ } }
  function handleMerged(kept, removedId) { setClients(cs => cs.filter(c => c.id !== removedId).map(c => (c.id === kept.id ? kept : c))); setMerging(null); }

  useEffect(() => {
    apiFetch('/clients')
      .then(setClients)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const filtered = clients.filter(c =>
    !search || c.name.toLowerCase().includes(search.toLowerCase()) ||
    (c.contact || '').toLowerCase().includes(search.toLowerCase())
  );

  function handleSave(saved) {
    setClients(cs => {
      const idx = cs.findIndex(c => c.id === saved.id);
      if (idx >= 0) { const next = [...cs]; next[idx] = saved; return next; }
      return [saved, ...cs];
    });
    setModal(null);
  }

  async function handleDelete(id) {
    setDeleting(true);
    try {
      await apiFetch(`/clients/${id}`, { method: 'DELETE' });
      setClients(cs => cs.filter(c => c.id !== id));
      setDeleteConfirm(null);
    } catch (ex) { alert(ex.message); } finally { setDeleting(false); }
  }

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Clients / GCs</h1>
          <p className="admin-page-sub">{clients.length} total</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {!loading && suggestionCount(clients) > 0 && (
            <button onClick={() => setReviewing(true)} title="Addresses, phones and faxes found on the internet — confirm each before it goes in"
              style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #bfdbfe', background: '#eff6ff', color: '#1d4ed8', fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>
              🔎 Review suggested updates ({suggestionCount(clients)})
            </button>
          )}
          {CAN_MERGE && (
            <button onClick={() => setMerging({ start: null, other: null })} title="Combine two customers that are really the same one"
              style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', color: '#374151', fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>🔗 Merge…</button>
          )}
          <button className="admin-primary-btn" onClick={() => setModal('new')}>+ New Client</button>
        </div>
      </div>
      {/* Possible duplicates — a strip that opens to the pairs, each with Merge or Not the same. */}
      {dupes.length > 0 && (
        <div className="admin-card" style={{ marginBottom: 12, padding: '10px 14px', borderColor: '#fde68a', background: '#fffbeb' }}>
          <button onClick={() => setShowDupes(v => !v)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 13.5, fontWeight: 800, color: '#92400e' }}>
            {showDupes ? '▼' : '▶'} Possible duplicates · {dupes.length}
          </button>
          {showDupes && dupes.map(d => (
            <div key={d.a.id + d.b.id} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 8, fontSize: 13 }}>
              <span style={{ flex: '1 1 300px' }}><b>{d.a.name}</b> and <b>{d.b.name}</b> <span style={{ color: '#9ca3af' }}>· {d.why}</span></span>
              <button onClick={() => setMerging({ start: d.a, other: d.b })} style={{ padding: '4px 11px', borderRadius: 6, border: '1px solid #1e3a5f', background: '#1e3a5f', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Merge…</button>
              <button onClick={() => notSame(d)} style={{ padding: '4px 11px', borderRadius: 6, border: '1px solid #e5e7eb', background: '#fff', color: '#6b7280', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Not the same</button>
            </div>
          ))}
        </div>
      )}
      {merging && <CustomerMerge clients={clients} start={merging.start} startOther={merging.other} onMerged={handleMerged} onClose={() => setMerging(null)} />}
      {reviewing && <CustomerSuggestions clients={clients} onSaved={saved => setClients(cs => cs.map(c => (c.id === saved.id ? saved : c)))} onClose={() => setReviewing(false)} />}

      <div className="admin-card" style={{ overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)' }}>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name or contact…"
            style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 12px', fontSize: 13, width: 280 }}
          />
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>No clients found</div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Company</th>
                <th>Phone</th>
                <th>Fax</th>
                <th>Address</th>
                <th style={{ width: 220 }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => (
                <tr key={c.id}>
                  <td><span className="admin-list-primary">{c.name}</span></td>
                  <td><span className="admin-list-secondary">{formatPhone(c.phone) || '—'}</span></td>
                  <td><span className="admin-list-secondary">{c.fax || '—'}</span></td>
                  <td>
                    <span className="admin-list-secondary">
                      {[c.address1, c.address2].filter(Boolean).join(', ') || '—'}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => setContactsFor(c)}
                        style={{ padding: '4px 10px', fontSize: 12, borderRadius: 6, border: '1px solid #a5b4fc', background: '#eef2ff', color: '#4338ca', cursor: 'pointer', fontWeight: 600 }}>
                        Contacts
                      </button>
                      <button onClick={() => setModal(c)}
                        style={{ padding: '4px 10px', fontSize: 12, borderRadius: 6, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer' }}>
                        Edit
                      </button>
                      {CAN_MERGE && (
                        <button onClick={() => setMerging({ start: c, other: null })} title="Merge this customer with another"
                          style={{ padding: '4px 10px', fontSize: 12, borderRadius: 6, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer' }}>Merge</button>
                      )}
                      <button onClick={() => setDeleteConfirm(c)}
                        style={{ padding: '4px 10px', fontSize: 12, borderRadius: 6, border: '1px solid #fca5a5', background: '#fff', color: '#dc2626', cursor: 'pointer' }}>
                        Del
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {(modal === 'new' || (modal && typeof modal === 'object')) && (
        <ClientModal
          client={modal === 'new' ? null : modal}
          onClose={() => setModal(null)}
          onSave={handleSave}
        />
      )}

      {contactsFor && (
        <ContactsModal client={contactsFor} onClose={() => setContactsFor(null)} />
      )}

      {deleteConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 28, width: 400, maxWidth: '100%' }}>
            <h3 style={{ margin: '0 0 10px', fontSize: 16, fontWeight: 700 }}>Delete Client?</h3>
            <p style={{ margin: '0 0 20px', fontSize: 14, color: '#6b7280' }}>
              "{deleteConfirm.name}" and all their contacts will be removed. Existing bids keep their contact snapshots.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setDeleteConfirm(null)} style={{ padding: '9px 18px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', fontSize: 13, cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={() => handleDelete(deleteConfirm.id)} disabled={deleting}
                style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: '#dc2626', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
